import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import publicApi from "@/lib/publicApi";
import {
  cartSubtotal,
  clearCart,
  getCart,
  storeTrackingPayload,
  storeTrackingSearch,
} from "@/lib/storeCart";
import { PublicStoreShell } from "@/components/store/PublicStoreShell";
import { aed } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function StoreCheckout() {
  const { branchCode } = useParams();
  const [searchParams] = useSearchParams();
  const qs = storeTrackingSearch(searchParams);
  const navigate = useNavigate();
  const cart = useMemo(() => getCart(branchCode), [branchCode]);
  const [mode, setMode] = useState("pickup");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data } = useQuery({
    queryKey: ["public-store", branchCode],
    queryFn: async () => {
      const { data: res } = await publicApi.get(`/public/store/${branchCode}`);
      return res;
    },
    enabled: Boolean(branchCode),
  });

  const store = data?.store ?? {};
  const subtotal = cartSubtotal(cart);
  let deliveryFee = 0;
  if (mode === "delivery") {
    deliveryFee = Number(store.delivery_fee || 0);
    if (store.free_delivery_above != null && subtotal >= Number(store.free_delivery_above)) {
      deliveryFee = 0;
    }
  }
  const total = subtotal + deliveryFee;

  async function submit(e) {
    e.preventDefault();
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setSubmitting(true);
    try {
      const tracking = storeTrackingPayload(searchParams);
      const { data: res } = await publicApi.post(`/public/store/${branchCode}/checkout/whatsapp`, {
        mode,
        customer_name: customerName,
        customer_phone: customerPhone,
        address: mode === "delivery" ? address : null,
        notes: notes || null,
        lines: cart.map((l) => ({
          menu_item_id: l.menu_item_id,
          product_id: l.menu_item_id,
          qty: l.qty,
        })),
        ...tracking,
      });
      clearCart(branchCode);
      toast.success(`Order ${res.order?.reference || ""} placed`);
      if (res.whatsapp_url) {
        window.open(res.whatsapp_url, "_blank", "noopener,noreferrer");
      }
      navigate(`/s/${branchCode}${qs}`);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.checkout?.[0] || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (cart.length === 0) {
    return (
      <PublicStoreShell>
        <div className="p-6 text-center space-y-4">
          <p className="text-sm text-[#8B6B5E]">Your cart is empty.</p>
          <Link to={`/s/${branchCode}/catalog${qs}`} className="text-[#F36F21] font-semibold text-sm">
            Back to menu
          </Link>
        </div>
      </PublicStoreShell>
    );
  }

  return (
    <PublicStoreShell>
      <form onSubmit={submit} className="flex min-h-[100dvh] flex-col px-4 py-4 gap-4">
        <div className="flex items-center justify-between">
          <Link to={`/s/${branchCode}/catalog${qs}`} className="text-[13px] font-semibold text-[#F36F21]">
            ← Menu
          </Link>
          <h1 className="text-[15px] font-extrabold text-[#2B0B04]">Checkout</h1>
          <span className="w-10" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {store.supports_pickup !== false && (
            <button
              type="button"
              onClick={() => setMode("pickup")}
              className={`rounded-xl border px-3 py-2.5 text-[13px] font-bold ${
                mode === "pickup" ? "border-[#F36F21] bg-[#FFF3E8] text-[#C2410C]" : "border-[#E8D5C8]"
              }`}
            >
              Pickup
            </button>
          )}
          {store.supports_delivery !== false && (
            <button
              type="button"
              onClick={() => setMode("delivery")}
              className={`rounded-xl border px-3 py-2.5 text-[13px] font-bold ${
                mode === "delivery" ? "border-[#F36F21] bg-[#FFF3E8] text-[#C2410C]" : "border-[#E8D5C8]"
              }`}
            >
              Delivery
            </button>
          )}
        </div>

        <div className="space-y-3 rounded-2xl border border-[#E8D5C8] bg-white p-4 shadow-sm">
          <div>
            <Label className="text-[12px]">Name</Label>
            <Input className="mt-1 h-10" required value={customerName} onChange={(e) => setCustomerName(e.target.value)} />
          </div>
          <div>
            <Label className="text-[12px]">Phone / WhatsApp</Label>
            <Input className="mt-1 h-10" required value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} />
          </div>
          {mode === "delivery" && (
            <div>
              <Label className="text-[12px]">Delivery address</Label>
              <Input className="mt-1 h-10" required value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
          )}
          <div>
            <Label className="text-[12px]">Notes</Label>
            <Input className="mt-1 h-10" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="rounded-2xl border border-[#E8D5C8] bg-white p-4 shadow-sm text-[13px] space-y-1.5">
          {cart.map((l, i) => (
            <div key={i} className="flex justify-between gap-2">
              <span className="truncate">{l.qty}× {l.name}</span>
              <span className="font-semibold shrink-0">{aed(l.qty * l.unit_price)}</span>
            </div>
          ))}
          <div className="border-t border-[#E8D5C8] pt-2 flex justify-between text-[#8B6B5E]">
            <span>Subtotal</span>
            <span>{aed(subtotal)}</span>
          </div>
          {mode === "delivery" && (
            <div className="flex justify-between text-[#8B6B5E]">
              <span>Delivery</span>
              <span>{aed(deliveryFee)}</span>
            </div>
          )}
          <div className="flex justify-between font-extrabold text-[#2B0B04] text-[15px]">
            <span>Total</span>
            <span>{aed(total)}</span>
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="h-12 rounded-2xl bg-[#F36F21] hover:bg-[#E05E10] text-white font-bold"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : "Place order via WhatsApp"}
        </Button>
        <p className="text-center text-[11px] text-[#8B6B5E]">
          Order goes to the restaurant Online/Call Orders board — not kitchen until staff converts to KOT.
        </p>
      </form>
    </PublicStoreShell>
  );
}
